// ** React Imports
import { useState, useEffect } from "react";

// ** MUI Imports
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  Divider,
  FormLabel,
  Tooltip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import CloseIcon from "@mui/icons-material/Close";

// ** Custom Component Imports
import CustomTextField from "components/mui/text-field";
import CustomAutocomplete from "components/mui/autocomplete";
import { SavePayment, getInvoices, getInvoicePayments } from "../logic/PaymentLogic";
import { uploadAttachment } from "screens/DispatchBoard/logic/DispatchBoardLogic";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

import Stripecheckout from "images/Stripecheckout.jpg";
import { CreateCheckoutSession } from "components/StripeConnect/logic/stripe-api";

const formatPrice = (val) => {
  if (val === null || val === undefined || val === "") return "";
  const num = typeof val === "number" ? val : Number(val);
  return isNaN(num)
    ? ""
    : `$${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  };
  
const RecordPaymentInvoice = ({ invoiceID, open, handleClose,fetchPayments }) => {
  const theme = useTheme();
  
  // State for invoices and fields
  const parsedInvoiceID = typeof invoiceID === 'string'
  ? parseInt(invoiceID, 10)
  : invoiceID;

  const [invoices, setInvoices] = useState([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(parsedInvoiceID > 0 ? parsedInvoiceID : "");
  const [invoiceData, setInvoiceData] = useState(null);
  const [payments, setPayments] = useState([]);

  // Payment info
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Card");
  const [paymentDate, setPaymentDate] = useState(dayjs());
  const [paidBy, setPaidBy] = useState("");
  const [note, setNote] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [remainingAmount, setRemainingAmount] = useState(0);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const calculateRemainingAmount = (invoiceData, payments) => {
    if (!invoiceData || !payments) return invoiceData?.totalAmount || 0;
    
    const totalPaid = payments
      .filter(payment => payment.paymentStatus === "Paid")
      .reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0);
  
    const totalRefunded = payments
      .filter(payment => payment.paymentStatus === "Refunded")
      .reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0);
  
    const invoiceTotal = Number(invoiceData?.totalAmount) || 0;
    const invoiceDepositUtilized = Number(invoiceData?.depositUtilizedAmount) || 0;
    const invoiceActual = invoiceTotal - invoiceDepositUtilized;
    const remaining = invoiceActual - totalPaid + totalRefunded;
    
    return Math.max(0, remaining); // Ensure it doesn't go negative
  };

  const handlePayment = async (paymentID) => {
    setIsProcessingPayment(true);

    try {
      const sessionResponse = await CreateCheckoutSession(paymentID);
      if (!sessionResponse?.result?.sessionId) {
        throw new Error("Failed to create session");
      }
      const sessionId = sessionResponse.result.sessionId;
      const CheckoutUrl = sessionResponse.result.url;
      window.location.href = CheckoutUrl; // Redirect to Stripe Checkout
    } catch (err) {
      console.error("Payment Error:", err.message);
      toast.error("Payment failed. Please try again.", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });// Show user feedback
    } finally {
      setIsProcessingPayment(false);
    }
  };


  const navigate = useNavigate();
  // Load all invoices on mount
  useEffect(() => {
    if (open) {
      // Reset form when dialog opens
      resetForm();
      
      if (parsedInvoiceID > 0) {
        // If we have a specific invoice ID, fetch its data
        fetchDataForInvoice(parsedInvoiceID);
      } else {
        // If no specific invoice ID (dropdown mode), just fetch invoices
        fetchInvoicesOnly();
      }
    }
  }, [open, parsedInvoiceID]);

  const fetchInvoicesOnly = async () => {
    try {
      const invoicesRes = await getInvoices();
      const filteredInvoices = (invoicesRes || []).filter(invoice => 
        invoice.status !== "Cancelled" && invoice.status !== "Paid"
      );
      
      setInvoices(filteredInvoices);
    } catch (err) {
      console.log("Error fetching invoices:", err);
    }
  };
  
  // FIX THE fetchDataForInvoice FUNCTION:
  const fetchDataForInvoice = async (invoiceId) => {
    if (!invoiceId || invoiceId === 0) return;
    
    try {
      // Fetch fresh invoice data
      const invoicesRes = await getInvoices();
      const filteredInvoices = (invoicesRes || []).filter(invoice => 
        invoice.status !== "Cancelled" && invoice.status !== "Paid"
      );
      
      setInvoices(filteredInvoices);
      
      // Find and set the current invoice
      const inv = filteredInvoices.find(i => i.invoiceID === invoiceId);
      setInvoiceData(inv || null);
      setSelectedInvoiceId(invoiceId);
      
      // Fetch payments for this invoice
      const paymentsRes = await getInvoicePayments(invoiceId);
      setPayments(paymentsRes || []);
      
      // Calculate remaining amount
      if (inv) {
        
        const remaining = calculateRemainingAmount(inv, paymentsRes || []);
        setRemainingAmount(remaining);
      }
    } catch (err) {
      console.log("Error fetching invoice data:", err);
    }
  };

  useEffect(() => {
    if (open && selectedInvoiceId && parsedInvoiceID === 0) {
      // This runs when user selects a different invoice from dropdown
      const fetchSelectedInvoiceData = async () => {
        const invoice = invoices.find(inv => inv.invoiceID === selectedInvoiceId);
        if (invoice) {
          
          setInvoiceData(invoice);
          
          // Fetch payments for the selected invoice
          const paymentsRes = await getInvoicePayments(selectedInvoiceId);
          setPayments(paymentsRes || []);
          
          // Calculate remaining amount
          const remaining = calculateRemainingAmount(invoice, paymentsRes || []);
          setRemainingAmount(remaining);
        }
      };
      
      fetchSelectedInvoiceData();
    }
  }, [selectedInvoiceId, open, parsedInvoiceID, invoices]);

  const allowedTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/bmp",
    "image/webp",
    "image/svg+xml",
  ];
  
  const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
  
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = [];
    let fileError = "";
  
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        fileError = "Only image files are allowed (jpg, png, gif, bmp, webp, svg).";
        break;
      }
      if (file.size > MAX_FILE_SIZE) {
        fileError = "Each file must be less than 2 MB.";
        break;
      }
      validFiles.push(file);
    }
  
    if (fileError) {
      toast.error(fileError);
      return;
    }
  
    setAttachments((prev) => [...prev, ...validFiles]);
  };

  // Reset all form fields
  const resetForm = () => {
    if (parsedInvoiceID === 0) {
      setSelectedInvoiceId("");
    }
    setPayments([]);
    setInvoiceData(null);
    setPaymentAmount("");
    setPaymentMethod("");
    setPaymentDate(dayjs());
    setPaidBy("");
    setNote("");
    setAttachments([]);
    setErrors({});
    setRemainingAmount(0);
  };

  // Submit payment
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = {};

    if (!selectedInvoiceId || selectedInvoiceId==="") newErrors.invoiceID = "Invoice is required.";
    if (!paymentAmount || Number(paymentAmount) <= 0) newErrors.paymentAmount = "Amount is required.";
    if (!paymentDate) newErrors.paymentDate = "Payment date is required.";
    if (!paymentMethod) newErrors.paymentMethod = "Payment method is required.";
    if (!paidBy) newErrors.paidBy = "Paid By is required.";
  
    setErrors(newErrors);
  
    const paymentStatus = (['Card', 'BankTransfer', 'ACH','ApplePay','GooglePay', 'Other'].includes(paymentMethod))? 'Pending' : 'Paid'
    // If any errors, do not submit
    if (Object.keys(newErrors).length > 0) {
      return;
    }

  const amtNum = Number(paymentAmount);
  if (amtNum > remainingAmount) {
    setErrors(prev => ({
      ...prev,
      paymentAmount: `Amount cannot exceed remaining balance of ${formatPrice(remainingAmount)}`
    }));
    return;
  }

    const payload = {
      paymentID: 0,
      paymentType: invoiceData?.invoiceType === "WorkOrderDeposit" ? "Deposit" : "Invoice",
      invoiceID: selectedInvoiceId,
      requestID: invoiceData?.requestID ?? 0,
      amount: Number(paymentAmount),
      paymentDate: paymentDate.format("YYYY-MM-DD"),
      paymentMethod,
      paymentStatus: paymentStatus,
      paidBy,
      notes: note,
    };

    setLoading(true); // Start loading

    try {
      const result = await SavePayment(payload, navigate);
      if (result && !result.isError) {
        if(!['Card','ACH', 'BankTransfer','ApplePay','GooglePay','Other'].includes(paymentMethod)){
          toast.success(result?.message || "Payment saved successfully.", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
        }                
        const paymentID = result.result?.paymentID;
        if(['Card','ACH', 'BankTransfer', 'ApplePay','GooglePay', 'Other'].includes(paymentMethod)){
          handlePayment(paymentID);          
        }
        if (attachments.length > 0 && paymentID) {
          for (const file of attachments) {
            try {
              await uploadAttachment(paymentID, file, "Payment");
            } catch (err) {
              console.log(err);              
            }
          }
        }                        
      } else {
        toast.error(result?.message || "Failed to save payment.", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
      }
    } catch(err) {      
      console.log(err);
    }finally{
      setLoading(false);
      fetchPayments(invoiceID);      
      resetForm();
      handleClose();
    }
  };
const formatNumberWithCommas = (value) => {
  if (value === null || value === undefined) return '';

  const str = value.toString();

  // Allow partial numbers while typing (e.g. ".", "0.", ".5")
  if (str === '' || str === '.' || str === '0.' || /^\.\d*$/.test(str)) {
    return str;
  }

  // Remove commas and format integer part
  const parts = str.replace(/,/g, '').split('.');
  if (parts[0] !== '') {
    const num = Number(parts[0]);
    // Check if it's actually a valid number before formatting
    if (!isNaN(num)) {
      parts[0] = num.toLocaleString('en-US');
    }
  }

  return parts.join('.');
};

const removeCommas = (value) => value.replace(/,/g, '');
  
  return (    
    <>
    <Dialog
      fullWidth={true}
      maxWidth="md"
      open={open}
      onClose={() => {
        handleClose();
        resetForm();
      }}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #D9D9D9",
          paddingBottom: "20px !important",
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Take a Payment - Invoice
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={5}>
          <Grid item xs={12}>
            <Typography variant="h4">Invoice Information</Typography>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ color: "text.disabled", "& .MuiDivider-wrapper": { px: 6 } }} />
          </Grid>
          <Grid item xs={12} md={5}>
            <CustomAutocomplete
              options={invoices}
              getOptionLabel={(option) => `INV-${option.invoiceID}`}
              value={
                invoices.find(opt => opt.invoiceID === selectedInvoiceId) || null
              }
              // onChange={(_, newValue) => {
              //   setSelectedInvoiceId(newValue ? newValue.invoiceID : "");
              //   setErrors((prev) => ({ ...prev, invoiceID: undefined })); // clear error on change
              // }}
              onChange={parsedInvoiceID === 0
                ? (_, newVal) => {
                    if (newVal && newVal.invoiceID) {
                      setSelectedInvoiceId(newVal.invoiceID);
                      setErrors(prev => ({ ...prev, invoiceID: undefined }));
                    }
                  }
                : undefined
              }
              disabled={parsedInvoiceID > 0}
              // disableClearable={false}
              disableClearable={parsedInvoiceID > 0}
              clearOnBlur={false}
              clearOnEscape
              // clearIcon={<CloseIcon fontSize="small" />}
              clearIcon={parsedInvoiceID === 0 ? <CloseIcon fontSize="small" /> : null}
              renderInput={(params) => (
                <CustomTextField
                  fullWidth
                  placeholder="Select"
                  label="Invoice ID"
                  error={Boolean(errors.invoiceID)}
                  helperText={errors.invoiceID}
                  {...params}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <CustomTextField
              fullWidth
              label="Invoice Amount"
              value={formatPrice(invoiceData?.totalAmount)}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <CustomTextField
              fullWidth
              label="Invoice Remaining Amount"
              value={formatPrice(remainingAmount)}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <FormLabel sx={{ fontWeight: 400, fontSize: "13px", marginBottom: "4px", color: "text.primary", height: "100%" }}>
              Invoice Date
            </FormLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                sx={{ width: "100%" }}
                value={invoiceData?.invoiceDate ? dayjs(invoiceData.invoiceDate) : null}
                slotProps={{ textField: { size: "small", readOnly: true } }}
                readOnly
                disabled
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={5}>
            <FormLabel sx={{ fontWeight: 400, fontSize: "13px", marginBottom: "4px", color: "text.primary", height: "100%" }}>
              Invoice Due Date
            </FormLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                sx={{ width: "100%" }}
                value={invoiceData?.dueDate ? dayjs(invoiceData.dueDate) : null}
                slotProps={{ textField: { size: "small", readOnly: true } }}
                readOnly
                disabled
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={5}>
            <CustomTextField
              fullWidth
              select
              label="Payment Terms"
              value={invoiceData?.additionalPaymentTerms?.[0] || ""}
              InputProps={{ readOnly: true }}
              disabled
            >
              <MenuItem value={invoiceData?.additionalPaymentTerms?.[0] || ""}>
                {invoiceData?.additionalPaymentTerms?.[0] || "N/A"}
              </MenuItem>
            </CustomTextField>
          </Grid>
          <Grid item xs={12} mt={4}>
            <Typography variant="h4">Customer Information</Typography>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ color: "text.disabled", "& .MuiDivider-wrapper": { px: 6 } }} />
          </Grid>
          <Grid item xs={12} md={5}>
            <CustomTextField
              fullWidth
              label="Customer Name"
              value={invoiceData?.customerName ?? ""}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <CustomTextField
              fullWidth
              label="Email"
              value={invoiceData?.customerEmail ?? ""}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <CustomTextField
              fullWidth
              label="Phone"
              value={invoiceData?.customerPhone ?? ""}
              InputProps={{ readOnly: true }}
            />
          </Grid>

          <Grid item xs={12} mt={4}>
            <Typography variant="h4">Payment Information</Typography>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ color: "text.disabled", "& .MuiDivider-wrapper": { px: 6 } }} />
          </Grid>
          <Grid item xs={12} md={5}>
           <CustomTextField
  fullWidth
  label="Amount"
  placeholder="Enter amount"
  type="text" // important: allows commas
  value={formatNumberWithCommas(paymentAmount || "")}
  onChange={(e) => {
    const rawValue = removeCommas(e.target.value); // remove commas before storing
    if (/^\d*\.?\d*$/.test(rawValue)) {
      setPaymentAmount(rawValue); // store clean number
    }
    setErrors((prev) => ({ ...prev, paymentAmount: undefined }));
  }}
  error={Boolean(errors.paymentAmount)}
  helperText={errors.paymentAmount}
  inputProps={{ inputMode: "decimal" }}
/>

          </Grid>
          <Grid item xs={12} md={5}>
            <FormLabel sx={{ fontWeight: 400, fontSize: "13px", marginBottom: "4px", color: "text.primary", height: "100%" }}>
              Payment Date
            </FormLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                sx={{ width: "100%" }}
                value={paymentDate}
                maxDate={dayjs()}
                onChange={value => {
                  setPaymentDate(value);
                  setErrors(prev => ({ ...prev, paymentDate: undefined }));
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    error: Boolean(errors.paymentDate),
                    helperText: errors.paymentDate,
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
              <Grid item xs={12} md={5}>
                <CustomAutocomplete
                  options={[
                    { value: 'ACH', label: 'ACH' },
                    { value: 'ApplePay', label: 'Apple Pay' },
                    { value: 'BankTransfer', label: 'Bank Transfer' },
                    { value: 'Card', label: 'Card' },
                    { value: 'Cash', label: 'Cash' },
                    { value: 'Check', label: 'Check' },
                    { value: 'GooglePay', label: 'Google Pay' },
                    { value: 'Other', label: 'Other' }
                  ]}
                  value={paymentMethod ? { value: paymentMethod, label: paymentMethod } : null}
                  onChange={(event, newValue) => {
                    setPaymentMethod(newValue?.value || '');
                    setErrors(prev => ({ ...prev, paymentMethod: undefined }));
                  }}
                  getOptionLabel={(option) => option.label}
                  renderInput={(params) => (
                    <CustomTextField
                      {...params}
                      label="Payment Method"
                      error={Boolean(errors.paymentMethod)}
                      helperText={errors.paymentMethod}
                      InputProps={{
                        ...params.InputProps,
                        placeholder: !paymentMethod ? "Select Payment Method" : "",
                      }}
                    />
                  )}
                  sx={{
                    '& .MuiAutocomplete-listbox': {
                      py: 0,
                      '& .MuiAutocomplete-option': {
                        py: 0.5,
                        fontSize: '0.875rem',
                        minHeight: '32px'
                      }
                    }
                  }}
                  PaperProps={{
                    sx: {
                      maxHeight: 200
                    }
                  }}
                />
              </Grid>
          <Grid item xs={12} md={5}>
            <CustomTextField
              fullWidth
              label="Paid By"
              placeholder="Enter name"
              value={paidBy}
              onChange={e => {
                setPaidBy(e.target.value);
                setErrors(prev => ({ ...prev, paidBy: undefined }));
              }}
              error={Boolean(errors.paidBy)}
              helperText={errors.paidBy}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <CustomTextField
              fullWidth
              label="Note"
              placeholder="Enter a Note"
              value={note}
              onChange={e => setNote(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <FormLabel sx={{ fontWeight: 400, fontSize: "13px", marginBottom: "4px", color: "text.primary", height: "100%" }}>
              Attachments
            </FormLabel>
            <Tooltip
              title={
                attachments.length
                  ? (
                    <Box>
                      {attachments.map((file, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            pb: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.primary"
                            sx={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            title={file.name}
                          >
                            {file.name}
                          </Typography>
                          <IconButton
                            size="small"
                            sx={{
                              p: "2px",
                              color: "#d32f2f",
                              '&:hover': { backgroundColor: '#fdecea' }
                            }}
                            onClick={e => {
                              e.stopPropagation();
                              setAttachments(prev => prev.filter((_, i) => i !== idx));
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )
                  : "No files selected"
              }
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#fff",
                    color: "#111",
                    boxShadow: 3,
                    fontSize: 13,
                    borderRadius: 1,
                    p: 1,
                  }
                },
                arrow: {
                  sx: { color: "#fff" }
                }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 4,
                  py: 2,
                  gap: 2,
                  border: '1px solid rgba(47, 43, 61, 0.2)',
                  borderRadius: 1,
                  cursor: 'pointer'
                }}
                onClick={() => document.getElementById("attachment-invoice-input").click()}
              >
                <input
                  id="attachment-invoice-input"
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif,.bmp,.webp,.svg"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                  multiple
                />
                <Typography>
                  {attachments.length === 0 ? "Drop files to attach or browse" : `${attachments.length} file(s) selected`}
                </Typography>
              </Box>
            </Tooltip>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "20px 24px !important",
          boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
          zIndex: 1,
        }}
        className="dialog-actions-dense"
      >
        <Box>
          <Button variant="contained" color="info" onClick={() => {
            handleClose();
            resetForm();
          }}
          disabled={loading}
          >
            Cancel
          </Button>
        </Box>
        <Box>
          <Button
            variant="contained"
            color="main"
            onClick={handleSubmit}
            disabled={loading}
            // startIcon={loading && <CircularProgress size={20} color="inherit" />}
          >
            {['Card', 'BankTransfer', 'ACH','ApplePay','GooglePay', 'Other'].includes(paymentMethod) ? 'Pay Now' : 'Save'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
    {/* Stripe Checkout Redirect Dialog */}
    <Dialog open={isProcessingPayment} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Redirect to Stripe Checkout
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <img src={Stripecheckout} alt="Stripe Checkout" style={{ maxWidth: '100%', height: 'auto' }} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body1" color="text.secondary">
              Redirecting to secure payment...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RecordPaymentInvoice;
